import React from 'react';
import { Link } from 'react-router-dom';
import { FileText, CreditCard, Wrench, CheckCircle, ArrowRight, HelpCircle, Loader2 } from 'lucide-react';
import { useCMSContent } from '../hooks/useCMSContent';

// Static tenant data (not editable via CMS as these are process-specific)
const tenantData = {
  applicationProcess: [
    "Browse available properties and submit an online application",
    "Complete our screening process including credit, background, and rental history checks",
    "Upon approval, sign your lease agreement and pay required deposits",
    "Schedule your move-in inspection and receive your keys"
  ],
  rentPayment: "Multiple payment options including online portal and automatic payments. Rent is due on the 1st of each month.",
  maintenanceRequests: "Submit requests through our online portal or call our office. Emergency maintenance available 24/7.",
  leaseExpectations: "Clear lease terms with fair policies. We explain everything before you sign."
};

export const TenantsPage = () => {
  // Fetch CMS content
  const { data: companyInfo, loading: companyLoading } = useCMSContent('companyInfo');

  return (
    <div style={{ minHeight: '100vh', backgroundColor: 'white' }} data-testid="tenants-page">
      {/* Hero */}
      <section style={{
        backgroundColor: 'var(--bg-section-light)',
        padding: '100px 0 80px'
      }}>
        <div className="container">
          <h1 className="heading-1" style={{ marginBottom: '24px', textAlign: 'center' }}>
            Information for Tenants
          </h1>
          <p className="body-large" style={{
            textAlign: 'center',
            maxWidth: '800px',
            margin: '0 auto',
            color: 'var(--text-secondary)'
          }}>
            Welcome! We're committed to providing responsive service and clear processes to make your rental experience smooth and professional.
          </p>
        </div>
      </section>

      {/* Application Process */}
      <section className="section-spacing">
        <div className="container">
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))',
            gap: '60px',
            alignItems: 'center'
          }}>
            <div>
              <h2 className="heading-2" style={{ marginBottom: '24px' }}>
                Application Process
              </h2>
              <p className="body-medium" style={{ marginBottom: '30px', color: 'var(--text-secondary)' }}>
                Our straightforward application process ensures quick processing while maintaining professional standards.
              </p>
              
              <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                {tenantData.applicationProcess.map((step, index) => (
                  <div key={index} style={{
                    display: 'flex',
                    gap: '20px',
                    padding: '20px',
                    backgroundColor: 'white',
                    borderRadius: '8px',
                    border: '1px solid var(--border-light)'
                  }}>
                    <div style={{
                      width: '40px',
                      height: '40px',
                      backgroundColor: 'var(--brand-primary)',
                      color: 'white',
                      borderRadius: '50%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontWeight: 700,
                      fontSize: '1.1rem',
                      flexShrink: 0
                    }}>
                      {index + 1}
                    </div>
                    <p style={{
                      color: 'var(--text-secondary)',
                      fontSize: '0.9375rem',
                      lineHeight: 1.6,
                      margin: 0,
                      paddingTop: '8px'
                    }}>
                      {step}
                    </p>
                  </div>
                ))}
              </div>
            </div>

            <div>
              <img
                src="https://customer-assets.emergentagent.com/job_3edf4311-b53d-4a6a-800b-8fbb025f103f/artifacts/tgvx8ehk_lrmg%20prop%207.jfif"
                alt="Modern Building"
                style={{
                  width: '100%',
                  height: '500px',
                  objectFit: 'cover',
                  borderRadius: '8px',
                  border: '1px solid var(--border-light)'
                }}
              />
            </div>
          </div>
        </div>
      </section>

      {/* Important Information - No Blue Boxes */}
      <section className="section-spacing" style={{ backgroundColor: 'var(--bg-section-light)' }}>
        <div className="container">
          <h2 className="heading-1" style={{ marginBottom: '50px', textAlign: 'center' }}>
            Tenant Resources
          </h2>

          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
            gap: '24px'
          }}>
            {[
              {
                icon: CreditCard,
                title: 'Rent Payments',
                description: tenantData.rentPayment
              },
              {
                icon: Wrench,
                title: 'Maintenance Requests',
                description: tenantData.maintenanceRequests
              },
              {
                icon: FileText,
                title: 'Lease Information',
                description: tenantData.leaseExpectations
              }
            ].map((item, index) => (
              <div
                key={index}
                style={{
                  backgroundColor: 'white',
                  padding: '32px',
                  borderRadius: '8px',
                  border: '1px solid var(--border-light)',
                  transition: 'transform 0.3s ease'
                }}
                className="card-hover"
              >
                <item.icon size={36} color="var(--brand-primary)" strokeWidth={1.5} style={{ marginBottom: '20px' }} />
                <h3 style={{
                  fontFamily: 'Inter, sans-serif',
                  fontWeight: 600,
                  fontSize: '1.125rem',
                  color: 'var(--text-primary)',
                  marginBottom: '12px'
                }}>
                  {item.title}
                </h3>
                <p style={{
                  color: 'var(--text-secondary)',
                  fontSize: '0.9375rem',
                  lineHeight: 1.7
                }}>
                  {item.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Expectations */}
      <section className="section-spacing">
        <div className="container">
          <div style={{
            backgroundColor: 'var(--bg-section-light)',
            padding: '60px',
            borderRadius: '8px',
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
            gap: '50px',
            alignItems: 'center'
          }}>
            <div>
              <h2 className="heading-2" style={{ marginBottom: '24px' }}>
                What to Expect
              </h2>
              <p className="body-medium" style={{ color: 'var(--text-secondary)' }}>
                We believe in clear communication and professional service. Here's what you can expect when renting from Laurel Run Management Group.
              </p>
            </div>

            <div style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '20px'
            }}>
              {[
                'Prompt response to maintenance requests',
                'Clear and professional communication',
                'Fair and consistent lease enforcement',
                'Respect for your privacy and rights',
                'Well-maintained properties'
              ].map((item, i) => (
                <div key={i} style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '15px'
                }}>
                  <CheckCircle size={24} color="var(--brand-primary)" style={{ flexShrink: 0 }} />
                  <span style={{
                    color: 'var(--text-secondary)',
                    fontSize: '0.9375rem'
                  }}>
                    {item}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section style={{
        background: 'linear-gradient(135deg, #1a3a52 0%, #2563eb 100%)',
        padding: '80px 0',
        textAlign: 'center'
      }}>
        <div className="container">
          <div style={{
            maxWidth: '700px',
            margin: '0 auto'
          }}>
            <HelpCircle size={60} color="white" style={{ marginBottom: '24px', display: 'inline-block' }} />
            <h2 style={{
              fontFamily: 'Inter, sans-serif',
              fontWeight: 700,
              fontSize: 'clamp(2rem, 2.5rem + 0.5vw, 2.75rem)',
              color: 'white',
              marginBottom: '16px'
            }}>
              Have Questions?
            </h2>
            <p style={{
              fontSize: '1.125rem',
              color: 'rgba(255, 255, 255, 0.9)',
              marginBottom: '32px'
            }}>
              Whether you're a current tenant or considering one of our properties, we're here to help. Reach out to our office with any questions or concerns.
            </p>
            <div style={{ display: 'flex', gap: '16px', justifyContent: 'center', flexWrap: 'wrap' }}>
              <Link to="/contact" className="btn-primary" style={{ backgroundColor: 'white', color: 'var(--brand-primary)' }}>
                Contact Our Office
                <ArrowRight size={20} style={{ marginLeft: '8px' }} />
              </Link>
              <a href={`mailto:${companyInfo?.email || 'Office@lrmgmt.net'}`} className="btn-secondary" style={{ backgroundColor: 'transparent', borderColor: 'white', color: 'white', border: '2px solid white' }}>
                Email Us
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};
